import type { ComponentPropsWithRef, ReactNode } from 'react';
import { cn } from '../lib/cn';
import {
    judgementTone,
    orderStatusLabel,
    orderStatusTone,
    severityLabel,
    severityTone,
    tone as toneMap,
    type JudgementTone,
    type OrderStatus,
    type Severity,
    type Tone,
    type TonePair,
} from '../tokens';

/**
 * Sizes as they appear in the prototype:
 * - `xs`  12px / 1px 7px  — a judgement in the report: 20px high with its border,
 *                           the same on a phone and on a desktop
 * - `sm`  11px / 4px 8px  — keuringen table, invoice table, EP source badges
 * - `md`  11px / 4px 9px  — public findings urgency, public parts list
 * - `lg`  12px / 5px 10px — "gedeeld met" pills, the "Rapport gereed" banner pill
 */
export type StatusPillSize = 'xs' | 'sm' | 'md' | 'lg';

const sizeStyles: Record<StatusPillSize, string> = {
    /* At least 20px with its border; a line height of its own that grows with a larger text setting. */
    xs: 'min-h-[20px] text-[12px] leading-[1.25] px-[7px] py-px',
    sm: 'text-[11px] px-2 py-1',
    md: 'text-[11px] px-[9px] py-1',
    lg: 'text-[12px] px-[10px] py-[5px]',
};

type PillOwnProps = {
    size?: StatusPillSize;
    /** Draw the tone's 1px border as well — used where the pill sits on a tinted block. */
    bordered?: boolean;
    /** Leading dot in the foreground colour ("Rapport gereed · 3 september 11:02"). */
    dot?: boolean;
    /** 500 for banner-style pills, 400 everywhere in tables and lists. */
    weight?: 'regular' | 'medium';
    children?: ReactNode;
};

/**
 * A pill states a status. It is never decorative, so it always carries a readable
 * label — colour alone never conveys meaning (principle 12).
 *
 * Exactly one of `status`, `severity`, `judgement` or `tone` must be given.
 * `judgement` is the report's own scale (Goed green … Niet waarneembaar blue),
 * drawn as a quiet label; it has no fallback label, because the word comes with
 * the report.
 */
export type StatusPillProps = Omit<ComponentPropsWithRef<'span'>, 'children'> &
    PillOwnProps &
    (
        | { status: OrderStatus; severity?: never; judgement?: never; tone?: never }
        | { severity: Severity; status?: never; judgement?: never; tone?: never }
        | { judgement: JudgementTone; status?: never; severity?: never; tone?: never }
        | { tone: Tone; status?: never; severity?: never; judgement?: never }
    );

function resolve(
    status?: OrderStatus,
    severity?: Severity,
    judgement?: JudgementTone,
    freeTone?: Tone,
): { pair: TonePair; fallbackLabel?: string } {
    if (status) return { pair: orderStatusTone[status], fallbackLabel: orderStatusLabel[status] };
    if (severity) return { pair: severityTone[severity], fallbackLabel: severityLabel[severity] };
    if (judgement) return { pair: judgementTone[judgement] };
    return { pair: toneMap[freeTone ?? 'neutral'] };
}

export function StatusPill(props: StatusPillProps) {
    const {
        size = 'sm',
        bordered = false,
        dot = false,
        weight = 'regular',
        className,
        children,
        status,
        severity,
        judgement,
        tone,
        ...rest
    } = props as Omit<ComponentPropsWithRef<'span'>, 'children'> &
        PillOwnProps & {
            status?: OrderStatus;
            severity?: Severity;
            judgement?: JudgementTone;
            tone?: Tone;
        };

    const { pair, fallbackLabel } = resolve(status, severity, judgement, tone);

    return (
        <span
            className={cn(
                'inline-flex items-center gap-2 whitespace-nowrap rounded-[20px]',
                'font-[family-name:var(--font-body)] leading-[1.3]',
                weight === 'medium' ? 'font-medium' : 'font-normal',
                bordered && 'border',
                sizeStyles[size],
                className,
            )}
            style={{
                background: pair.bg,
                color: pair.fg,
                borderColor: bordered ? (pair.border ?? pair.bg) : undefined,
            }}
            {...rest}
        >
            {dot ? (
                <span
                    aria-hidden="true"
                    className="h-[7px] w-[7px] shrink-0 rounded-full"
                    style={{ background: pair.fg }}
                />
            ) : null}
            {children ?? fallbackLabel}
        </span>
    );
}
